import { Rng } from './rng.js';
import { Board, Edge, Harbor, Hex, Resource, Terrain, Vertex } from './types.js';

const TERRAIN_POOL: Terrain[] = [
  ...Array<Terrain>(4).fill('wood'),
  ...Array<Terrain>(4).fill('sheep'),
  ...Array<Terrain>(4).fill('wheat'),
  ...Array<Terrain>(3).fill('brick'),
  ...Array<Terrain>(3).fill('ore'),
  'desert',
];

const TOKEN_POOL = [2, 3, 3, 4, 4, 5, 5, 6, 6, 8, 8, 9, 9, 10, 10, 11, 11, 12];

const HARBOR_POOL: Harbor['kind'][] = ['any', 'any', 'any', 'any', 'wood', 'brick', 'sheep', 'wheat', 'ore'];

/** Positions (in perimeter order) of the 9 harbor edges among the 30 coastal edges. */
const HARBOR_SLOTS = [0, 3, 7, 10, 13, 17, 20, 23, 27];

const SQRT3 = Math.sqrt(3);

/** Axial coordinates of the 19 hexes (radius 2), row by row from the top. */
function hexCoords(): Array<{ q: number; r: number }> {
  const coords: Array<{ q: number; r: number }> = [];
  for (let r = -2; r <= 2; r++) {
    for (let q = -2; q <= 2; q++) {
      if (Math.abs(q + r) <= 2) coords.push({ q, r });
    }
  }
  return coords;
}

/**
 * Build the fixed geometry (hexes, vertices, edges, adjacency) for the standard 19-hex island.
 * Pointy-top hexes with unit circumradius.
 */
export function buildGeometry(): Pick<Board, 'hexes' | 'vertices' | 'edges'> {
  const hexes: Hex[] = [];
  const vertices: Vertex[] = [];
  const edges: Edge[] = [];
  const vertexByKey = new Map<string, number>();
  const edgeByKey = new Map<string, number>();

  const vertexAt = (x: number, y: number): number => {
    const key = `${Math.round(x * 1000)},${Math.round(y * 1000)}`;
    let id = vertexByKey.get(key);
    if (id === undefined) {
      id = vertices.length;
      vertexByKey.set(key, id);
      vertices.push({ id, x, y, hexes: [], edges: [], neighbors: [], harbor: null });
    }
    return id;
  };

  const edgeBetween = (a: number, b: number): number => {
    const [lo, hi] = a < b ? [a, b] : [b, a];
    const key = `${lo}-${hi}`;
    let id = edgeByKey.get(key);
    if (id === undefined) {
      id = edges.length;
      edgeByKey.set(key, id);
      edges.push({ id, vertices: [lo, hi], hexes: [] });
      vertices[lo].edges.push(id);
      vertices[hi].edges.push(id);
      vertices[lo].neighbors.push(hi);
      vertices[hi].neighbors.push(lo);
    }
    return id;
  };

  for (const { q, r } of hexCoords()) {
    const id = hexes.length;
    const x = SQRT3 * (q + r / 2);
    const y = 1.5 * r;
    const corners: number[] = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 180) * (60 * i - 90);
      corners.push(vertexAt(x + Math.cos(angle), y + Math.sin(angle)));
    }
    hexes.push({ id, q, r, x, y, terrain: 'desert', token: null, vertices: corners });
    for (const v of corners) vertices[v].hexes.push(id);
    for (let i = 0; i < 6; i++) {
      const e = edgeBetween(corners[i], corners[(i + 1) % 6]);
      edges[e].hexes.push(id);
    }
  }

  return { hexes, vertices, edges };
}

/** Coastal edges (touching exactly one hex), ordered clockwise around the island. */
export function coastalEdges(geometry: Pick<Board, 'vertices' | 'edges'>): number[] {
  const angleOf = (e: Edge) => {
    const [a, b] = e.vertices.map((v) => geometry.vertices[v]);
    return Math.atan2((a.y + b.y) / 2, (a.x + b.x) / 2);
  };
  return geometry.edges
    .filter((e) => e.hexes.length === 1)
    .sort((a, b) => angleOf(a) - angleOf(b))
    .map((e) => e.id);
}

function hexesAreAdjacent(a: Hex, b: Hex): boolean {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return Math.max(Math.abs(dq), Math.abs(dr), Math.abs(dq + dr)) === 1;
}

/** Generate a random board. Red numbers (6 and 8) are never placed on neighboring hexes. */
export function generateBoard(rng: Rng): Board {
  const { hexes, vertices, edges } = buildGeometry();

  const terrains = rng.shuffle(TERRAIN_POOL);
  hexes.forEach((h, i) => (h.terrain = terrains[i]));

  const producing = hexes.filter((h) => h.terrain !== 'desert');
  for (let attempt = 0; ; attempt++) {
    const tokens = rng.shuffle(TOKEN_POOL);
    producing.forEach((h, i) => (h.token = tokens[i]));
    const hot = producing.filter((h) => h.token === 6 || h.token === 8);
    const clash = hot.some((a) => hot.some((b) => a !== b && hexesAreAdjacent(a, b)));
    if (!clash || attempt > 1000) break;
  }

  const coast = coastalEdges({ vertices, edges });
  const kinds = rng.shuffle(HARBOR_POOL);
  const harbors: Harbor[] = HARBOR_SLOTS.map((slot, i) => ({ kind: kinds[i], edge: coast[slot] }));
  harbors.forEach((h, i) => {
    for (const v of edges[h.edge].vertices) vertices[v].harbor = i;
  });

  return { hexes, vertices, edges, harbors };
}

export function terrainResource(t: Terrain): Resource | null {
  return t === 'desert' ? null : t;
}
